import {TextEditorServices} from '@frontapp/editor-html';
import {SideObject} from '@popperjs/core';
import {flatMap} from 'lodash';
import memoize from 'lru-memoize';
import React, {Component, Fragment, ReactNode} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/helpers/src/internationalization/react';
import {
  FrontEvent,
  Handler,
  HandlerOf,
  Renderer,
} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownProps} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {isTeamNamespace} from '../../../../helpers/models/namespaceHelpers';
import {AsyncStatusFromResourceTableStatus} from '../../../../helpers/resourceTableStatusHelpers';
import {
  AnswerFolderModel,
  AnswerOrFolderModel,
  isAnswerFolder,
} from '../../../../models/api/answerFolderModel';
import {AnswerModel} from '../../../../models/api/answerModel';
import {TeamModel} from '../../../../models/api/teamModel';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../../../services/tracking';
import {VariableInterpolator} from '../../../../services/variableInterpolation/variableInterpolator';
import {AsyncStatus, ResourceMap, ResourceTableStatusesEnum} from '../../../../store/appTypes';
import {DropdownCategory} from '../../../common/dropdowns/dropdownCategory';
import {DropdownEmpty} from '../../../common/dropdowns/dropdownEmpty';
import {DropdownLoader} from '../../../common/dropdowns/dropdownLoader';
import {DeprecatedSearchDropdown} from '../../../common/dropdowns/search/deprecatedSearchDropdown';
import {RowHeightPredicate, RowRendererOf} from '../../../common/dropdowns/source/dropdownSource';
import {SyncDropdownSource} from '../../../common/dropdowns/source/syncDropdownSource';
import {FolderBreadcrumb, FolderBreadcrumbs} from '../../../folderBreadcrumbs/folderBreadcrumbs';
import {AnswersDropdownCreateRow} from './answersDropdownCreateRow';
import {AnswersDropdownEmpty} from './answersDropdownEmpty';
import {
  AnswersDropdownAnswerTypesEnum,
  AnswersDropdownItemTypesEnum,
  buildAnswerFolderDropdownItem,
  buildCreateNewAnswerDropdownItem,
  buildCurrentFolderAnswersDropdownItem,
  buildHeaderDropdownItem,
  buildRecentAnswerDropdownItem,
  DropdownItem,
  DropdownItemAnswer,
  DropdownItemAnswerFolder,
  isDropdownItemAnswerFolder,
} from './answersDropdownItems';
import {AnswersDropdownRow} from './answersDropdownRow';
import {AnswersFolderDropdownRow} from './answersFolderDropdownRow';
import {searchAnswersAndFolders} from './answersSearchHelpers';
import {answerPreviewPopoverWidth} from './preview/answerPreview';

/*
 * Props and State.
 */

export interface AnswersDropdownProps extends DropdownProps {
  allAnswers: ReadonlyArray<AnswerModel>;
  allFolders: ReadonlyArray<AnswerFolderModel>;
  teams: ResourceMap<TeamModel>;
  loadingStatus: AsyncStatus;
  fetchAllAnswersAndFolders: Handler;
  onAnswerSelect?: HandlerOf<AnswerModel>;
  onCreateClick: Handler;
  editorServices: TextEditorServices;
  recentAnswers: ReadonlyArray<AnswerModel>;
  addRecentAnswerId: HandlerOf<number>;
  interpolator: VariableInterpolator;
  messageUid: string;
  isQuickInsert: boolean;
  onBackButtonClick?: Handler;
  hasQuickInsert: boolean;
}

interface AnswersDropdownState {
  searchQuery: string;
  currentFolder: AnswerFolderModel | undefined;
}

/*
 * Constants.
 */

const answersDropdownHeight = 430;
const preventOverflowPadding: Partial<SideObject> = {
  // Position ourselves far enough from the right edge to accommodate the preview dropdown.
  right: answerPreviewPopoverWidth,
};
const dropdownHeader = <Trans id="dropdown-responses-header">Message templates</Trans>;

/*
 * Styles.
 */

const StyledChevronIcon = styled(Icon)`
  display: inline-block;
  vertical-align: text-bottom;
  margin: 0px 2px 1px;
`;

/*
 * Component.
 */

export class AnswersDropdown extends Component<AnswersDropdownProps, AnswersDropdownState> {
  private readonly editorText: string;
  private readonly isEditorEmpty: boolean;

  constructor(props: AnswersDropdownProps) {
    super(props);
    this.state = {
      searchQuery: '',
      currentFolder: undefined,
    };
    this.editorText = this.props.editorServices.text.text;
    this.isEditorEmpty = this.props.editorServices.empty.isEmpty;
  }

  componentDidMount() {
    this.maybeReloadAnswersAndFolders();
  }

  /*
   * Event handlers.
   */

  private onAnswerSelect(item: DropdownItemAnswer) {
    const {onAnswerSelect, addRecentAnswerId, messageUid} = this.props;

    addRecentAnswerId(item.answer.id);

    if (!onAnswerSelect) {
      return;
    }

    trackInterfaceEvent(InterfaceEventsEnum.CANNED_ANSWER_INSERT, {
      origin: 'dropdown',
      id: item.answer.id,
      hasSearch: Boolean(this.state.searchQuery),
      answerType: item.answerType,
      folder_id: item.answer.folderId,
      in_folder: Boolean(item.answer.folderId),
      message_uid: messageUid,
      is_quick_insert: this.props.isQuickInsert,
    });

    onAnswerSelect(item.answer);
  }

  onAnswerFolderClick = (event: FrontEvent, folder: AnswerFolderModel) => {
    event.preventDefault();
    this.maybeReloadAnswersAndFolders();
    this.setState({searchQuery: '', currentFolder: folder});
  };

  onBackButtonClick = () => {
    if (!this.state.currentFolder) {
      this.props.onBackButtonClick?.();
      return;
    }

    this.setState((previousState, props) => {
      const newCurrentFolderId: number | undefined = previousState.currentFolder?.parentFolderId;

      const newCurrentFolder = newCurrentFolderId
        ? props.allFolders.find((folder) => folder.id === newCurrentFolderId)
        : undefined;

      return {
        currentFolder: newCurrentFolder,
      };
    });
  };

  onSearchQueryChange = (searchQuery: string) => {
    this.maybeReloadAnswersAndFolders();
    this.setState({searchQuery});
  };

  /*
   * Render.
   */

  render() {
    const shouldShowBackButton = this.computeShouldShowBackButton();

    const backButtonLabel = shouldShowBackButton ? (
      <Trans id="dropdown-responses-back">Back</Trans>
    ) : undefined;

    if (this.props.hasQuickInsert && !this.props.allAnswers.length) {
      return (
        <AnswersDropdownEmpty
          {...this.props} /* eslint-disable-line react/jsx-props-no-spreading */
          title={dropdownHeader}
          editorText={this.props.editorServices.text.text}
          backButtonLabel={backButtonLabel}
          onBackButtonClick={this.onBackButtonClick}
        />
      );
    }

    const items = this.makeDropdownItems();
    const dropdownSource = new SyncDropdownSource({
      items,
      rowFilter: (allItems, searchQuery) =>
        searchQuery ? this.makeSearchResultsDropdownItems(searchQuery.toLowerCase()) : allItems,
      renderRow: this.renderRow,
      rowHeightPredicate: this.getRowHeight,
      renderEmpty: this.renderEmptyOrLoader,
      refreshKey: this.state.currentFolder ? String(this.state.currentFolder.id) : undefined,
    });

    const defaultHighlightedIndex = this.computeDefaultHighlightedIndex(items);

    return (
      <DeprecatedSearchDropdown
        {...this.props} /* eslint-disable-line react/jsx-props-no-spreading */
        title={dropdownHeader}
        height={answersDropdownHeight}
        width={300}
        preventOverflowPadding={preventOverflowPadding}
        source={dropdownSource}
        searchQuery={this.state.searchQuery}
        onSearchQueryChange={this.onSearchQueryChange}
        defaultHighlightedIndex={defaultHighlightedIndex}
        backButtonLabel={backButtonLabel}
        onBackButtonClick={this.onBackButtonClick}
      />
    );
  }

  private computeShouldShowBackButton() {
    if (this.props.isQuickInsert) {
      return true;
    }

    return !this.state.searchQuery && this.state.currentFolder;
  }

  private computeDefaultHighlightedIndex(items: ReadonlyArray<DropdownItem>) {
    if (this.props.isQuickInsert) {
      // In Quick Insert, the "create" button is hidden, so if there are message
      // templates, then item 0 is a heading (either "Recent" or "All") and item
      // 1 is the first template option.
      return items.length > 1 ? 1 : undefined;
    }

    // In the non-Quick Insert variant, the answers dropdown has a "create"
    // header as the 0th item, then the "create" button as the 1st item, and
    // then it's the same as above in Quick Insert. So the 3rd item will be the
    // first message template.
    return items.length > 3 ? 3 : undefined;
  }

  private readonly getRowHeight: RowHeightPredicate<DropdownItem> = (item, index, answers) => {
    // Headers should have a height of 30.
    if (item?.type === AnswersDropdownItemTypesEnum.HEADER) {
      return 30;
    }

    // The create row should have a height of 30 if there's no preview.
    if (item?.type === AnswersDropdownItemTypesEnum.CREATE && this.isEditorEmpty) {
      return 30;
    }

    // All other rows should have a height of 48.
    return 48;
  };

  private readonly renderEmptyOrLoader: Renderer = () => {
    if (
      this.props.loadingStatus.status === ResourceTableStatusesEnum.LOADED ||
      this.props.loadingStatus.status === ResourceTableStatusesEnum.INITIAL
    ) {
      return <DropdownEmpty />;
    }

    return (
      <DropdownLoader
        size={VisualSizesEnum.LARGE}
        status={AsyncStatusFromResourceTableStatus[this.props.loadingStatus.status]}
        onRetryClick={this.props.fetchAllAnswersAndFolders}
      />
    );
  };

  private readonly renderRow: RowRendererOf<DropdownItem> = (item, index, allItems) => {
    const buildAnswerInteractionId = (answerItem: DropdownItemAnswer) => {
      switch (answerItem.answerType) {
        case AnswersDropdownAnswerTypesEnum.RECENT:
          return `recentAnswers.${answerItem.answer.id}`;

        case AnswersDropdownAnswerTypesEnum.CURRENT_FOLDER:
          return `allAnswers.${answerItem.answer.id}`;

        default:
          return undefined;
      }
    };

    switch (item.type) {
      case AnswersDropdownItemTypesEnum.HEADER:
        return <DropdownCategory>{item.title()}</DropdownCategory>;

      case AnswersDropdownItemTypesEnum.CREATE:
        return (
          <AnswersDropdownCreateRow
            editorText={this.editorText}
            key="save"
            onSelect={this.props.onCreateClick}
            index={index}
            isFromDraft={!this.props.isQuickInsert}
          />
        );

      case AnswersDropdownItemTypesEnum.ANSWER:
        return (
          <AnswersDropdownRow
            key={item.answer.id}
            onClick={() => this.onAnswerSelect(item)}
            index={index}
            answer={item.answer}
            interpolator={this.props.interpolator}
            onDropdownClose={this.props.onRequestClose}
            interactionId={buildAnswerInteractionId(item)}
            folderBreadcrumbs={this.renderSimpleBreadcrumbsForFolderId(item.answer.folderId)}
            isSearchResult={Boolean(this.state.searchQuery)}
            highlight={item.highlight}
            showPreview={!this.props.isQuickInsert}
          />
        );

      case AnswersDropdownItemTypesEnum.FOLDER:
        return (
          <AnswersFolderDropdownRow
            key={item.folder.id}
            onClick={(event: FrontEvent) => this.onAnswerFolderClick(event, item.folder)}
            index={index}
            folder={item.folder}
            folderBreadcrumbs={this.renderSimpleBreadcrumbsForFolderId(item.folder.parentFolderId)}
            isSearchResult={Boolean(this.state.searchQuery)}
            folderTeam={this.props.teams[item.folder.namespace]}
            showTeamAvatar={hasSameFolderNameAsAnotherTeam(item, allItems)}
          />
        );

      default:
        throw new Error('This should not happen.');
    }
  };

  private readonly makeDropdownItems = (): ReadonlyArray<DropdownItem> => {
    if (
      this.props.loadingStatus.status !== ResourceTableStatusesEnum.LOADED &&
      this.props.loadingStatus.status !== ResourceTableStatusesEnum.INITIAL
    ) {
      return [];
    } // We render the loading spinner/error state in `renderEmptyOrLoader`.

    return this.makeLoadedDropdownItems();
  };

  private readonly makeLoadedDropdownItems = (): ReadonlyArray<DropdownItem> => {
    const items: Array<DropdownItem> = [];

    if (!this.state.currentFolder) {
      // Create section.
      if (!this.props.isQuickInsert || !this.props.allAnswers.length) {
        items.push(this.createNewAnswerHeaderDropdownItem);
        items.push(buildCreateNewAnswerDropdownItem());
      }

      // Recent section.
      if (this.props.recentAnswers.length) {
        items.push(this.recentHeaderDropdownItem);
        items.push(...this.buildRecentAnswerDropdownItems());
      }
    }

    // All/"Current Folder" section.
    items.push(this.buildBreadcrumbHeaderDropdownItem());
    items.push(...this.buildFolderDropdownItemsForCurrentFolder());
    items.push(...this.buildAnswerDropdownItemsForCurrentFolder());

    return items;
  };

  private readonly makeSearchResultsDropdownItems = (searchQuery: string): ReadonlyArray<DropdownItem> => {
    const allAnswersAndFolders = [...this.props.allAnswers, ...this.props.allFolders];

    const answerAndFolderSearchResults = searchAnswersAndFolders(allAnswersAndFolders, searchQuery);

    return this.buildAnswerAndFolderDropdownItems(answerAndFolderSearchResults);
  };

  private readonly buildRecentAnswerDropdownItems = (): ReadonlyArray<DropdownItemAnswer> =>
    this.props.recentAnswers.map(buildRecentAnswerDropdownItem);

  private readonly buildAnswerDropdownItemsForCurrentFolder = (): ReadonlyArray<DropdownItemAnswer> => {
    const answersInCurrentFolder = this.props.allAnswers.filter(
      (answer) => answer.folderId === this.state.currentFolder?.id,
    );
    return answersInCurrentFolder.map((answer) => buildCurrentFolderAnswersDropdownItem(answer));
  };

  private readonly buildFolderDropdownItemsForCurrentFolder = () => {
    const answerFoldersInCurrentFolder = this.props.allFolders.filter(
      (folder) => folder.parentFolderId === this.state.currentFolder?.id,
    );
    return answerFoldersInCurrentFolder.map(buildAnswerFolderDropdownItem);
  };

  private readonly createNewAnswerHeaderDropdownItem = buildHeaderDropdownItem(
    <Trans id="dropdown-responses-section-create">Create</Trans>,
  );

  private readonly recentHeaderDropdownItem = buildHeaderDropdownItem(
    <Trans id="dropdown-responses-section-recent">Recent</Trans>,
  );

  private readonly buildAnswerAndFolderDropdownItems = (
    answersAndFolders: ReadonlyArray<AnswerOrFolderModel>,
  ): ReadonlyArray<DropdownItemAnswer | DropdownItemAnswerFolder> =>
    answersAndFolders.map((answerOrFolder) =>
      isAnswerFolder(answerOrFolder)
        ? buildAnswerFolderDropdownItem(answerOrFolder)
        : buildCurrentFolderAnswersDropdownItem(answerOrFolder),
    );

  private readonly buildBreadcrumbHeaderDropdownItem = () =>
    buildHeaderDropdownItem(
      <FolderBreadcrumbs
        topLevelBreadcrumb={this.buildFolderBreadcrumb(
          <Trans id="dropdown-responses-section-all">All</Trans>,
          this.onTopLevelBreadcrumbClick,
        )}
        folderPathBreadcrumbs={this.buildFolderPathBreadcrumbs(this.state.currentFolder)}
      />,
    );

  // These are "simple" breadcrumbs in that it doesn't have any ellipsis'ing logic.
  // This is mean to be displayed in a tooltip or the answer/folder description during search.
  // The `FolderBreadcrumbs` component is used for the more complicated truncation logic elsewhere.
  private readonly renderSimpleBreadcrumbsForFolderId = (folderId?: number): ReadonlyArray<ReactNode> => {
    const folder = this.getFolder(folderId);
    const breadcrumbs = this.buildFolderPathBreadcrumbs(folder);

    // This breadcrumb is not expected to change while it is displayed, so using the index here for the key is fine.
    return flatMap(
      breadcrumbs,
      (breadcrumb: FolderBreadcrumb, index: number): ReactNode => [
        <Fragment key={`${breadcrumb.label}${index}`}>{breadcrumb.label}</Fragment>,
        <StyledChevronIcon key={`chevron${index}`} name="chevronRightTiny" />,
      ],
    ).slice(0, -1);
  };

  private readonly buildFolderPathBreadcrumbs = (
    folder?: AnswerFolderModel,
  ): ReadonlyArray<FolderBreadcrumb> => {
    if (!folder) {
      return [];
    }

    const currentFolderBreadcrumb = this.buildFolderBreadcrumb(
      folder.name,
      (event: FrontEvent | undefined) => {
        this.onFolderBreadcrumbClick(event, folder);
      },
    );

    const parentFolder = this.getFolder(folder.parentFolderId);
    return [...this.buildFolderPathBreadcrumbs(parentFolder), currentFolderBreadcrumb];
  };

  private readonly buildFolderBreadcrumb = (
    label: ReactNode,
    onBreadcrumbClick: HandlerOf<FrontEvent | undefined>,
  ): FolderBreadcrumb => ({
    label,
    onBreadcrumbClick,
  });

  private readonly getFolder = (folderId?: number) =>
    this.props.allFolders.find((folder) => folder.id === folderId);

  private readonly onTopLevelBreadcrumbClick = (event: FrontEvent | undefined) => {
    this.onFolderBreadcrumbClick(event, undefined);
  };

  private readonly onFolderBreadcrumbClick = (event: FrontEvent | undefined, folder?: AnswerFolderModel) => {
    event?.preventDefault();
    this.maybeReloadAnswersAndFolders();
    this.setState({searchQuery: '', currentFolder: folder});
  };

  // Data might be stale, not loaded, or in an error state, and require refetching from the server.
  private readonly maybeReloadAnswersAndFolders = () => {
    if (this.props.loadingStatus.status === ResourceTableStatusesEnum.LOADING) {
      return;
    } // Already reloading.

    if (
      this.props.loadingStatus.status === ResourceTableStatusesEnum.INITIAL ||
      this.props.loadingStatus.isStale ||
      this.props.loadingStatus.status === ResourceTableStatusesEnum.FAILED
    ) {
      this.props.fetchAllAnswersAndFolders();
    }
  };
}

/*
 * Helpers.
 */

function hasSameFolderNameAsAnotherTeam(
  folderItem: DropdownItemAnswerFolder,
  allItems: ReadonlyArray<DropdownItem>,
): boolean {
  if (!isTopLevelFolder(folderItem.folder) || !isTeamNamespace(folderItem.folder.namespace)) {
    return false;
  }

  const duplicates = getTopLevelTeamFolderDuplicates(allItems);
  return duplicates.has(folderItem.folder.name);
}

function isTopLevelFolder(folder: AnswerFolderModel): boolean {
  return folder.parentFolderId === undefined;
}

const getTopLevelTeamFolderDuplicates = memoize(20)((allItems: ReadonlyArray<DropdownItem>): Set<string> => {
  const topLevelFolders = allItems
    .filter(isDropdownItemAnswerFolder)
    .map((folderItem) => folderItem.folder)
    .filter(isTopLevelFolder);

  return getDuplicatesAcrossTeams(topLevelFolders);
});

function getDuplicatesAcrossTeams(folders: ReadonlyArray<AnswerFolderModel>): Set<string> {
  const folderNames = new Set<string>();
  const folderNameDuplicates = new Set<string>();

  folders.forEach((folder) => {
    if (!isTeamNamespace(folder.namespace)) {
      return;
    }

    if (folderNames.has(folder.name)) {
      folderNameDuplicates.add(folder.name);
      return;
    }

    folderNames.add(folder.name);
  });

  return folderNameDuplicates;
}
